import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  RotateCcw,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Wrench,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Logo from "@/app/icons/Logo";
import { cn } from "@/lib/utils";
import { AppDrawer } from "@/components/app/AppDrawer";
import { LogoutDialog } from "./LogoutDialog";
import { NotificationPopover } from "./NotificationPopover";
import { useSidebar } from "@/components/app/SidebarContext";
import { authService } from "@/api/auth";
import { useAuthStore } from "@/store/useAuthStore";

const navItems = [
  { label: "Dashboard", href: "/", icon: LayoutDashboard },
  { label: "Product Management", href: "/product-management", icon: Package },
  { label: "Order Management", href: "/order-management", icon: ShoppingCart },
  { label: "Repair Management", href: "/repair-management", icon: Wrench },
  // { label: "Returns", href: "/returns", icon: RotateCcw },
  { label: "Account Settings", href: "/account-settings", icon: Settings },
];

function NavList({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { pathname } = useLocation();

  return (
    <nav className="space-y-2">
      {navItems.map(({ label, href, icon: Icon }) => {
        const active = pathname === href;
        return (
          <Link
            key={href}
            to={href}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            className={cn(
              "flex items-center gap-3 px-5 py-3 h-12 md:h-[59px] rounded-lg text-base font-medium transition-colors",
              active
                ? "bg-foreground text-background"
                : "text-foreground/70 hover:bg-foreground/10 hover:text-foreground",
              collapsed && "justify-center px-0",
            )}
          >
            <Icon size={18} className="shrink-0" />
            {!collapsed && (
              <span className="whitespace-nowrap truncate">{label}</span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

function LogOutItem({ collapsed }: { collapsed: boolean }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);

  const handleConfirmLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      logout();
      navigate("/login");
    }
  };

  return (
    <>
      <div className="sticky bottom-0 z-10 bg-background">
        <button
          onClick={() => setOpen(true)}
          title={collapsed ? "Log Out" : undefined}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-base border border-destructive h-12 md:h-[59px] font-medium text-destructive hover:bg-destructive/10 transition-colors w-full",
            collapsed && "justify-center px-0",
          )}
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span>Log Out</span>}
        </button>
      </div>

      <LogoutDialog
        open={open}
        onOpenChange={setOpen}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
}

function DesktopSidebar() {
  const { collapsed, setCollapsed, setCollapsedByBtn, collapsedByBtn } =
    useSidebar();

  // Show expanded if: not collapsed OR (collapsed but hovering)
  const isExpanded = !collapsed;

  const handleToggle = () => {
    const nextState = !collapsedByBtn;
    setCollapsed(nextState);
    setCollapsedByBtn(nextState);
  };

  return (
    <aside
      className={cn(
        "h-full flex flex-col shrink-0 border border-border-light bg-background relative transition-[width] duration-300 ease-in-out rounded-lg",
      )}
    >
      {/* Logo area */}
      <div
        className={cn(
          "flex items-center h-[90px] p-2 border-b border-border-light px-4 overflow-hidden relative",
          isExpanded ? "justify-start" : "justify-center px-0",
        )}
      >
        <Link to="/" className="shrink-0">
          <Logo isCollapsed={!isExpanded} />
        </Link>

        {/* Collapse toggle — only show when expanded */}

        <button
          onClick={handleToggle}
          aria-label="Collapse sidebar"
          className={`absolute right-3 top-1/2 -translate-y-1/2 z-20 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background shadow-sm hover:bg-accent transition-all duration-300 cursor-pointer ${collapsedByBtn ? "rotate-180" : ""} ${isExpanded ? "" : "opacity-0"}`}
        >
          <ChevronRight size={12} />
        </button>
      </div>

      <div
        className={
          "flex flex-col justify-between py-5 pl-5 pr-2 h-[calc(100%-90px)] overflow-auto"
        }
      >
        {/* Nav */}
        <div className="w-full pb-3">
          <NavList collapsed={!isExpanded} />
        </div>

        {/* Log out */}
        <div className="w-full">
          <LogOutItem collapsed={!isExpanded} />
        </div>
      </div>
    </aside>
  );
}

function MobileDrawer() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        className="lg:hidden flex items-center justify-center h-9 w-9 rounded-lg border border-border hover:bg-accent transition-colors"
      >
        <Menu size={18} />
      </button>

      <AppDrawer
        open={open}
        onClose={() => setOpen(false)}
        direction="left"
        size={320}
      >
        <button
          onClick={() => setOpen(false)}
          aria-label="Close menu"
          className="flex items-center justify-center h-9 w-9 hover:bg-accent transition-colors ml-2 shrink-0 cursor-pointer absolute right-1 top-1 bg-background border border-border rounded-full p-1"
        >
          <X size={18} />
        </button>
        <div className="px-4 py-3 overflow-auto">
          <div className="border border-border-light rounded-lg">
            <div className="flex items-center justify-center h-auto md:h-[90px] border-b border-border-light shrink-0 p-3">
              <Link to="/" onClick={() => setOpen(false)}>
                <Logo isCollapsed={false} />
              </Link>
            </div>

            <div className="h-[calc(100dvh-120px)] overflow-auto flex flex-col justify-between py-5 px-3">
              <div className="w-full pb-3">
                <NavList collapsed={false} onNavigate={() => setOpen(false)} />
              </div>

              <div className="w-full">
                <LogOutItem collapsed={false} />
              </div>
            </div>
          </div>
        </div>
      </AppDrawer>
    </>
  );
}

export { DesktopSidebar, MobileDrawer };
